import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import * as moment from 'moment';
import { Subscription, Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { FormGroup, FormControl } from '@angular/forms';
import { first, map } from 'rxjs/operators';

import { IAccount } from '@account-users';
import { Permission } from '@account-users/account/models/Permission';

import { getUserPermissions } from '@account-users/user/state';
import { LoadPermissions } from '@account-users/user/state/permissions/permissions.actions';

import { DownloadCSV } from '../../actions/email.actions';
import {
  GetAccounts,
  GetEmailStatistics,
  GetSmsStatistics,
  Clear,
} from '../../actions/delivery-statistics.actions';
import {
  Toggle,
  ToggleType,
  Statistics,
  TimeSpanType,
  TimeSpanDropdownItem,
  ChartData,
  StatisticsType,
  Tool,
} from '../../models';
import {
  getStatisticsOverTime,
  getStatistics,
  getDomainStatistics,
  getLoading,
  getAccounts,
  getSendingsStatistics,
  getCountryCodesStatistics,
} from '../../reducers';
import { ActivatedRoute } from '@angular/router';
import { State } from '../../reducers/delivery-statistics.reducer';

const LAST_DEC_2018 = 1546297199999;
const ONE_YEAR = 31622400000; // 366 days
const SIX_MONTHS = 15897600000 + 3600000; // 184 days + one hour
const THIRTY_DAYS = 2592000000 + 3600000; // 30 days + one hour
const ONE_WEEK = 604800000; // 7 days
const ONE_DAY = 86400000; // 24 hours
// the extra hour is added to some of the constant to make up for daylight savings time

@Component({
  selector: 'app-delivery-statistics',
  templateUrl: './delivery-statistics.component.html',
  styleUrls: ['./delivery-statistics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryStatisticsComponent
  implements OnDestroy, AfterViewChecked {
  domains$: Observable<Statistics[]>;
  sendings$: Observable<Statistics[]>;
  countryCodes$: Observable<Statistics[]>;
  includeTrigger$ = new BehaviorSubject<boolean>(false);
  includeTrigger1$ = new BehaviorSubject<{
    type: ToggleType;
    selected: boolean;
  }>({ type: ToggleType.None, selected: false });
  includeTrigger2$ = new BehaviorSubject<{
    type: ToggleType;
    selected: boolean;
  }>({ type: ToggleType.None, selected: false });
  hasTransactionEmail$: Observable<boolean>;

  TimeSpanType = TimeSpanType;
  StatisticsType = StatisticsType;
  Tool = Tool;

  tool = Tool.Email;
  interval = 'hour';
  accounts: IAccount[] = [];
  filteredAccounts: IAccount[] = [];
  loading = false;
  statistics = {} as Statistics;
  chartData = {} as ChartData;
  statisticsTimeSpan: Statistics[] = [];
  rangeForm = new FormGroup(
    {
      from: new FormControl({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getDate(),
      }),
      to: new FormControl({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getDate(),
      }),
    },
    (formGroup) => {
      const { from, to } = formGroup.value;
      const momentFrom = moment(
        `${from.year}-${from.month}-${from.day}`,
        'YYYY-MM-DD',
      );
      const momentTo = moment(`${to.year}-${to.month}-${to.day}`, 'YYYY-MM-DD');

      return {
        ...(!(
          momentFrom.isValid() &&
          momentTo.isValid() &&
          this.validateEmailRange(momentFrom, momentTo)
        ) && { invalidDate: true }),
        ...(!(
          momentFrom.isSameOrBefore(moment.now()) &&
          momentTo.isSameOrBefore(moment.now())
        ) && { futureError: true }),
        ...(!momentFrom.isSameOrBefore(momentTo) && { toAfterFrom: true }),
      };
    },
  );
  accountControl = new FormControl('');
  filterControl = new FormControl('');
  timeSpanControl = new FormControl(TimeSpanType.ONE_DAY);
  timeValues: TimeSpanDropdownItem[] = [
    { name: 'Last 24 hours', value: TimeSpanType.ONE_DAY },
    { name: 'Last 7 days', value: TimeSpanType.ONE_WEEK },
    { name: 'Last 30 days', value: TimeSpanType.ONE_MONTH },
    { name: 'Last 6 months', value: TimeSpanType.SIX_MONTHS },
    { name: 'Last 12 months', value: TimeSpanType.TWELVE_MONTHS },
    { name: 'All time', value: TimeSpanType.ALL_TIME },
    { name: 'Range', value: TimeSpanType.RANGE },
  ];

  public toggles: Toggle[] = [
    {
      text: 'Include Marketing Automation sendings',
      type: ToggleType.MarketingAutomationSending,
      visible: true,
    },
    {
      text: 'Include Transactional sendings',
      type: ToggleType.TransactionalEmailSending,
      visible: false,
      permission: Permission.TransactionalTeamBackOfficePreLaunch,
    },
  ];
  public sendingsToggles$: Observable<Toggle[]>;

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly store: Store<State>,
    private readonly route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    @Inject('webVersionUrl') private webVersionUrl: string,
  ) {
    this.route.params.subscribe(({ tool }) => {
      this.store.dispatch(new Clear());
      this.tool = tool;
      if (tool === Tool.Email) {
        this.timeValues = [
          { name: 'Last 24 hours', value: TimeSpanType.ONE_DAY },
          { name: 'Last 7 days', value: TimeSpanType.ONE_WEEK },
          { name: 'Range', value: TimeSpanType.RANGE },
        ];
      }
    });

    this.sendingsToggles$ = this.store.pipe(
      select(getUserPermissions),
      first((permissions) => !!(permissions && permissions.length)),
      map((permissions) => {
        return this.toggles.map((toggle) => ({
          ...toggle,
          visible: toggle.permission
            ? permissions.includes(toggle.permission)
            : toggle.visible,
        }));
      }),
    );

    this.store.dispatch(new GetAccounts({ searchTerm: '', loadAll: true }));
    this.store.dispatch(new LoadPermissions());

    this.countryCodes$ = this.store.pipe(select(getCountryCodesStatistics));
    this.domains$ = this.store.pipe(select(getDomainStatistics));

    this.sendings$ = combineLatest([
      this.store.pipe(select(getSendingsStatistics)),
      this.includeTrigger1$,
      this.includeTrigger2$,
    ]).pipe(
      map(([sendings, includeTrigger1, includeTrigger2]) => {
        const activeToggles = { [ToggleType.EmailBulkSending]: true };
        if (includeTrigger1.selected)
          activeToggles[includeTrigger1.type] = true;
        if (includeTrigger2.selected)
          activeToggles[includeTrigger2.type] = true;
        return sendings.filter((sending) => activeToggles[sending.type]);
      }),
    );

    this.subscriptions = [
      this.store
        .pipe(select(getStatisticsOverTime))
        .subscribe(
          (statisticsTimeSpan) =>
            (this.statisticsTimeSpan = statisticsTimeSpan),
        ),

      this.store.pipe(select(getStatistics)).subscribe((statistics) => {
        this.statistics = statistics;
        this.chartData = this.generateChartData(statistics);
      }),

      this.store
        .pipe(select(getLoading))
        .subscribe((loading) => (this.loading = loading)),

      this.store.pipe(select(getAccounts)).subscribe((accounts) => {
        this.accounts = accounts;
        this.filteredAccounts = accounts;
      }),

      this.filterControl.valueChanges.subscribe(
        (value) =>
          (this.filteredAccounts = this.accounts.filter((a) =>
            new RegExp(value, 'i').test(`${a.displayName}${a.id}`),
          )),
      ),
    ];
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  getError() {
    let errorMsg = '';
    if (this.rangeForm.invalid) {
      const { invalidDate, futureError, toAfterFrom } = this.rangeForm.errors;
      switch (true) {
        case invalidDate:
          errorMsg =
            'You have selected a wide date range, please select dates within 7 days of each other.';
          break;
        case futureError:
          errorMsg =
            'You have selected dates in the future, please select todays date or before.';
          break;
        case toAfterFrom:
          errorMsg = 'From date should be before or same as to date.';
          break;
        default:
          errorMsg = '';
          break;
      }
    }
    return errorMsg;
  }

  validateEmailRange(from, to) {
    if (this.tool === Tool.Email) {
      return to.diff(from, 'days') < 8;
    }
    return true;
  }

  updateStatistics() {
    const timeSpanType = this.timeSpanControl.value;
    const accountId = this.accountControl.value;

    if (timeSpanType === TimeSpanType.RANGE && this.rangeForm.invalid) {
      return;
    }

    const timeSpans = this.getTimeSpans(timeSpanType);

    const actions = {
      [Tool.Email]: new GetEmailStatistics({ timeSpans, accountId }),
      [Tool.Sms]: new GetSmsStatistics({ timeSpans, accountId }),
    };

    this.store.dispatch(actions[this.tool]);
  }

  generateChartData(statistics: Statistics) {
    const {
      sent,
      normalizedDelivered,
      uniqueBounce,
      click,
      uniqueClick,
      open,
      uniqueOpen,
    } = statistics;

    return {
      sent: [
        { label: 'Sent', value: sent, color: '#1B7A91', showInChart: false },
        {
          label: 'Delivered',
          value: normalizedDelivered,
          color: '#72C3B2',
          showInChart: true,
        },
        {
          label: 'Bounced',
          value: uniqueBounce,
          color: '#E2696A',
          showInChart: true,
        },
      ],
      clicks: [
        { label: 'Clicks', value: click, color: '#F89A4D', showInChart: true },
        {
          label: 'Unique clicks',
          value: uniqueClick,
          color: '#7D72C3',
          showInChart: true,
        },
      ],
      opens: [
        { label: 'Opens', value: open, color: '#5BB3BD', showInChart: true },
        {
          label: 'Unique opens',
          value: uniqueOpen,
          color: '#569285',
          showInChart: true,
        },
      ],
    };
  }

  getFromToTimeStamps(
    endOfToken: 'hour' | 'day',
    subtractAmount: number,
    subtractToken: 'hour' | 'day' | 'month',
  ) {
    return {
      from: moment()
        .endOf(endOfToken)
        .subtract(subtractAmount, subtractToken)
        .valueOf(),
      to: moment().endOf(endOfToken).valueOf(),
    };
  }

  getFromTo(type: TimeSpanType) {
    switch (type) {
      case TimeSpanType.ONE_DAY:
        return this.getFromToTimeStamps('hour', 24, 'hour');
      case TimeSpanType.ONE_WEEK:
        return this.getFromToTimeStamps('day', 7, 'day');
      case TimeSpanType.ONE_MONTH:
        return this.getFromToTimeStamps('day', 30, 'day');
      case TimeSpanType.SIX_MONTHS:
        return this.getFromToTimeStamps('day', 6, 'month');
      case TimeSpanType.TWELVE_MONTHS:
        return this.getFromToTimeStamps('day', 12, 'month');
      case TimeSpanType.ALL_TIME:
        return {
          from: LAST_DEC_2018,
          to: moment().endOf('day').valueOf(),
        };
      case TimeSpanType.RANGE: {
        const { from, to } = this.rangeForm.value;
        return {
          from: moment(`${from.year}-${from.month}-${from.day}`, 'YYYY-MM-DD')
            .startOf('day')
            .subtract(1, 'millisecond')
            .valueOf(),
          to: moment(`${to.year}-${to.month}-${to.day}`, 'YYYY-MM-DD')
            .endOf('day')
            .valueOf(),
        };
      }
    }
  }

  getInterval(from: number, to: number) {
    const diff = to - from;
    switch (true) {
      case diff > ONE_YEAR:
        return 'year';
      case diff > SIX_MONTHS:
        return 'month';
      case diff > THIRTY_DAYS:
        return 'week';
      case diff > ONE_WEEK:
      case diff > ONE_DAY:
        return 'day';
      default:
        return 'hour';
    }
  }

  getTimestamps(
    from: number,
    to: number,
    interval: 'year' | 'month' | 'week' | 'day' | 'hour',
  ) {
    const isoInterval = interval === 'week' ? 'isoWeek' : interval;
    const plusOne = (timestamp: number) =>
      moment(timestamp).add(1, interval).startOf(isoInterval).valueOf();

    const timestamps = [];

    for (let step = plusOne(from); step < to; step = plusOne(step)) {
      if (step - from > 1) {
        timestamps.push(step);
      }
    }

    return [from, ...timestamps, moment(to).add(1, 'millisecond').valueOf()];
  }

  getTimeSpans(type: TimeSpanType): number[] {
    const { from, to } = this.getFromTo(type);
    const interval = this.getInterval(from, to);
    this.interval = interval;

    return this.getTimestamps(from, to, interval);
  }

  resetFilters() {
    this.accountControl.setValue('');
    this.filterControl.setValue('');
    this.timeSpanControl.setValue(TimeSpanType.ONE_DAY);
  }

  openWebVersion({ accountId, activityId }: Statistics) {
    window.open(
      `${this.webVersionUrl}${activityId}?accountId=${accountId}`,
      '_blank',
    );
  }

  downloadCSV(type: StatisticsType) {
    this.store.dispatch(new DownloadCSV(type));
  }
}
